import React, { useMemo } from 'react';
import { ChartCard, generateChartColors, InfoTooltip, PieChart } from 'ui-v2';

import { type FragmentType, gql, useFragment } from '@/generated';
import { useChartTranslations } from '@/hooks/useChartTranslations';
import orderBy from 'lodash/orderBy';

export const Index_CountryIncomeGroupFragment = gql(/* GraphQL */ `
  fragment Index_CountryIncomeGroupFragment on HealthcareIndicators {
    incomeGroup {
      items {
        count
        label
        pct
      }
      isDataSuppressed
    }
  }
`);

type CountryIncomeGroupProps = {
  className?: string;
  indicators: FragmentType<typeof Index_CountryIncomeGroupFragment>;
};

export function CountryIncomeGroup({ indicators, className }: CountryIncomeGroupProps) {
  const { incomeGroup } = useFragment(Index_CountryIncomeGroupFragment, indicators);

  const { title, subtitle, chartTooltip, getOptionLabel, getOptionTooltip, getOptionDisplayValue } =
    useChartTranslations('incomeGroup');

  const pieData = useMemo(() => {
    const colors = generateChartColors(incomeGroup.items.length);
    const sorted = orderBy(
      incomeGroup.items
        .filter((item) => !!item)
        .map((item) => ({
          label: getOptionLabel(item?.label),
          tooltipLabel: getOptionTooltip(item?.label),
          value: item?.pct,
          displayValue: getOptionDisplayValue(item.count, {
            pct: item.pct.toLocaleString('en', { maximumFractionDigits: 1 }),
          }),
        })),
      ['value'],
      ['desc']
    );
    return sorted.map((x, i) => ({ ...x, color: colors[i] }));
  }, [incomeGroup.items]);

  return (
    <ChartCard className={className}>
      <ChartCard.Header>
        <ChartCard.Title>
          {title}
          {chartTooltip && <InfoTooltip>{chartTooltip}</InfoTooltip>}
        </ChartCard.Title>
        {subtitle && <ChartCard.Subtitle>{subtitle}</ChartCard.Subtitle>}
      </ChartCard.Header>
      <ChartCard.Body isDataSuppressed={incomeGroup.isDataSuppressed}>
        <PieChart data={pieData} showLegend chartPosition="right" />
      </ChartCard.Body>
    </ChartCard>
  );
}
